<template>
  <v-flex>
    <v-layout row wrap>
      <v-flex xs12>
        <h2 class="primary--text pb-2">Account Features</h2>
        <p>
          Turn catalog features on or off for this account. A feature is off when it has no
          row in account features. Features with settings show an arrow next to the name.
        </p>
      </v-flex>
      <v-flex v-if="loading" xs12 class="mt-3">
        <p>Loading features…</p>
      </v-flex>
      <v-flex v-if="loadError" xs12 class="mt-3">
        <p class="error--text">{{ loadError }}</p>
      </v-flex>
      <v-flex v-if="!loading && !loadError && features.length === 0" xs12 class="mt-3">
        <p>No features are defined.</p>
      </v-flex>
      <v-flex
        v-for="feature in features"
        :key="feature.code"
        xs12
        class="feature-item"
      >
        <div class="feature-line">
          <v-switch
            v-model="feature.enabled"
            color="secondary"
            :label="feature.title"
            hide-details
            class="feature-switch mt-0"
            @change="onToggle(feature)"
          ></v-switch>
          <button
            v-if="feature.enabled && hasOptions(feature.code)"
            type="button"
            class="feature-expand"
            :aria-label="isOpen(feature.code) ? 'Hide ' + feature.title + ' options' : 'Show ' + feature.title + ' options'"
            @click="toggleOpen(feature.code)"
          >
            <v-icon color="secondary">{{ isOpen(feature.code) ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
          </button>
        </div>
        <div v-if="feature.enabled && hasOptions(feature.code) && isOpen(feature.code)" class="feature-detail">
          <p v-if="feature.description" class="mb-2">{{ feature.description }}</p>
          <edit-code-pane
            v-if="feature.code === 'lifecycle_callbacks'"
            :code="lifeCycleCodeValue"
            language="javascript"
            v-on:update-code="updateLifecycleCalls"
          ></edit-code-pane>
          <div v-else-if="feature.code === 'oidc'">
            <v-text-field
              v-model="feature.options.issuer"
              box
              label="Issuer URL"
              hint="OIDC discovery issuer, e.g. https://auth.example.com/application/o/caseblocks"
              persistent-hint
              class="mb-2"
              @input="emitOidc(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.clientId"
              box
              label="Client ID"
              class="mb-2"
              @input="emitOidc(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.clientSecret"
              box
              :type="showOidcSecret ? 'text' : 'password'"
              label="Client secret"
              :placeholder="feature.options.clientSecretConfigured ? '•••••••• (stored — enter a new secret to replace)' : ''"
              :hint="feature.options.clientSecretConfigured ? 'Leave blank to keep the stored secret.' : 'Required for SSO until a secret is stored.'"
              persistent-hint
              append-icon="visibility"
              class="mb-2"
              @click:append="showOidcSecret = !showOidcSecret"
              @input="emitOidc(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.buttonLabel"
              box
              label="Sign-in button label"
              hint="Shown on the login page. Default is Sign in with SSO."
              persistent-hint
              class="mb-2"
              @input="emitOidc(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.scopes"
              box
              label="Scopes"
              hint="Space-separated. Default is openid email profile."
              persistent-hint
              class="mb-2"
              @input="emitOidc(feature)"
            ></v-text-field>
            <v-autocomplete
              v-model="feature.options.defaultTeam"
              :items="teams"
              item-text="displayName"
              item-value="displayName"
              box
              clearable
              label="Default team"
              hint="Team for users created on first sign-in."
              persistent-hint
              class="mb-2"
              @change="emitOidc(feature)"
            ></v-autocomplete>
            <v-switch
              v-model="feature.options.autoCreateUsers"
              label="Create users on first sign-in"
              color="secondary"
              @change="emitOidc(feature)"
            ></v-switch>
            <v-switch
              v-model="feature.options.disablePasswordLogin"
              label="Disable password login"
              color="secondary"
              @change="emitOidc(feature)"
            ></v-switch>
          </div>
          <div v-else-if="feature.code === 'activedirectory'">
            <v-text-field
              v-model="feature.options.ldap_url"
              box
              label="LDAP URL"
              hint="Used when LDAP_URL is not set on the server."
              persistent-hint
              class="mb-2"
              @input="emitActiveDirectory(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.username"
              box
              label="Bind username"
              class="mb-2"
              @input="emitActiveDirectory(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.password"
              box
              :type="showAdPassword ? 'text' : 'password'"
              label="Bind password"
              :placeholder="feature.options.passwordConfigured ? '•••••••• (stored — enter a new password to replace)' : ''"
              :hint="feature.options.passwordConfigured ? 'Leave blank to keep the stored password.' : ''"
              persistent-hint
              append-icon="visibility"
              class="mb-2"
              @click:append="showAdPassword = !showAdPassword"
              @input="emitActiveDirectory(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.baseDN"
              box
              label="Base DN"
              class="mb-2"
              @input="emitActiveDirectory(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.principalNameSuffix"
              box
              label="Principal name suffix"
              hint="Appended to the login name, for example @company.com."
              persistent-hint
              class="mb-2"
              @input="emitActiveDirectory(feature)"
            ></v-text-field>
            <v-text-field
              v-model="feature.options.defaultTeam"
              box
              label="Default team"
              class="mb-2"
              @input="emitActiveDirectory(feature)"
            ></v-text-field>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import EditCodePane from "@/components/shared/editCodePane.vue"
import { provider } from "@/provider.js"
import { mapState } from "vuex"

const OPTION_CODES = {
  lifecycle_callbacks: true,
  oidc: true,
  activedirectory: true,
}

const attrsObject = (attrs) => {
  if (!attrs) {
    return {}
  }
  if (typeof attrs === "string") {
    try {
      const parsed = JSON.parse(attrs)
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed
      }
    } catch (e) {
      return {}
    }
    return {}
  }
  if (typeof attrs === "object" && !Array.isArray(attrs)) {
    return attrs
  }
  return {}
}

const oidcOptionsFrom = (account, attrs) => {
  const sso = account && account.oidcSso ? account.oidcSso : null
  const raw = attrsObject(attrs)
  const nested = raw.oidc && typeof raw.oidc === "object" ? raw.oidc : raw
  if (sso) {
    return {
      issuer: sso.issuer || "",
      clientId: sso.clientId || "",
      clientSecret: "",
      clientSecretConfigured: !!sso.clientSecretConfigured,
      buttonLabel: sso.buttonLabel || "Sign in with SSO",
      scopes: sso.scopes || "openid email profile",
      defaultTeam: sso.defaultTeam || "",
      autoCreateUsers: !!sso.autoCreateUsers,
      disablePasswordLogin: !!sso.disablePasswordLogin,
    }
  }
  return {
    issuer: nested.issuer || "",
    clientId: nested.client_id || nested.clientId || "",
    clientSecret: "",
    clientSecretConfigured: !!(nested.client_secret || nested.clientSecret),
    buttonLabel: nested.button_label || nested.buttonLabel || "Sign in with SSO",
    scopes: nested.scopes || "openid email profile",
    defaultTeam: nested.default_team || nested.defaultTeam || "",
    autoCreateUsers: !!(nested.auto_create_users || nested.autoCreateUsers),
    disablePasswordLogin: !!(nested.disable_password_login || nested.disablePasswordLogin),
  }
}

const activeDirectoryOptionsFrom = (attrs) => {
  const raw = attrsObject(attrs)
  return {
    ldap_url: raw.ldap_url || "",
    username: raw.username || "",
    password: "",
    passwordConfigured: !!raw.passwordConfigured,
    baseDN: raw.baseDN || "",
    principalNameSuffix: raw.principalNameSuffix || "",
    defaultTeam: raw.defaultTeam || "",
  }
}

export default {
  name: "account-features",
  components: { EditCodePane },
  props: ["account", "featuresSaveVersion"],
  data: () => ({
    features: [],
    originalEnabled: {},
    loading: false,
    loadError: "",
    open: {},
    showOidcSecret: false,
    showAdPassword: false,
  }),
  computed: {
    ...mapState({
      teams: function(state) {
        return state.teams && state.teams.all ? state.teams.all : []
      },
    }),
    lifeCycleCodeValue: {
      get() {
        if (this.account && this.account.lifeCycleCallbacks) {
          return this.account.lifeCycleCallbacks
        }
        return ""
      },
      set(value) {
        this.$emit("update-account-data", { key: "lifeCycleCallbacks", value: value })
      },
    },
  },
  watch: {
    featuresSaveVersion() {
      this.loadFeatures()
    },
  },
  created() {
    this.loadFeatures()
  },
  methods: {
    async loadFeatures() {
      this.loading = true
      this.loadError = ""
      try {
        const rows = await provider.accountFeatureToggles()
        const features = []
        const originalEnabled = {}
        const list = rows || []
        for (let i = 0; i < list.length; i++) {
          const row = list[i]
          originalEnabled[row.code] = !!row.enabled
          features.push({
            code: row.code,
            title: row.title,
            description: row.description,
            enabled: !!row.enabled,
            options: this.optionsFor(row.code, row.attrs),
          })
        }
        this.features = features
        this.originalEnabled = originalEnabled
      } catch (err) {
        this.loadError = err && err.message ? err.message : "Unable to load features."
      }
      this.loading = false
    },
    hasOptions(code) {
      return !!OPTION_CODES[code]
    },
    optionsFor(code, attrs) {
      if (code === "oidc") {
        return oidcOptionsFrom(this.account, attrs)
      }
      if (code === "activedirectory") {
        return activeDirectoryOptionsFrom(attrs)
      }
      return {}
    },
    isOpen(code) {
      return !!this.open[code]
    },
    toggleOpen(code) {
      this.$set(this.open, code, !this.open[code])
    },
    onToggle(feature) {
      if (!feature.enabled) {
        this.$set(this.open, feature.code, false)
      }
      this.emitToggles()
    },
    emitToggles() {
      this.$nextTick(() => {
        const pending = {}
        for (let i = 0; i < this.features.length; i++) {
          const feature = this.features[i]
          if (!!feature.enabled !== !!this.originalEnabled[feature.code]) {
            pending[feature.code] = !!feature.enabled
          }
        }
        this.$emit("update-account-data", { key: "changedAccountFeatures", value: pending })
      })
    },
    updateLifecycleCalls(update) {
      this.lifeCycleCodeValue = update
    },
    emitOidc(feature) {
      const options = feature.options || {}
      const payload = {
        enabled: true,
        issuer: options.issuer || "",
        clientId: options.clientId || "",
        buttonLabel: options.buttonLabel || "",
        scopes: options.scopes || "",
        autoCreateUsers: !!options.autoCreateUsers,
        defaultTeam: options.defaultTeam || "",
        disablePasswordLogin: !!options.disablePasswordLogin,
      }
      if (options.clientSecret && String(options.clientSecret).trim()) {
        payload.clientSecret = String(options.clientSecret).trim()
      }
      this.$emit("update-account-data", { key: "changedOidcSso", value: payload })
    },
    emitActiveDirectory(feature) {
      const options = feature.options || {}
      const attrs = {
        ldap_url: options.ldap_url || "",
        username: options.username || "",
        baseDN: options.baseDN || "",
        principalNameSuffix: options.principalNameSuffix || "",
        defaultTeam: options.defaultTeam || "",
      }
      if (options.password && String(options.password).trim()) {
        attrs.password = String(options.password).trim()
      }
      const current = this.account && this.account.changedFeatureAttrs ? this.account.changedFeatureAttrs : {}
      const next = Object.assign({}, current)
      next.activedirectory = attrs
      this.$emit("update-account-data", { key: "changedFeatureAttrs", value: next })
    },
  },
}
</script>

<style scoped>
.feature-item {
  margin-top: 4px;
}
.feature-line {
  display: flex;
  align-items: center;
}
.feature-switch {
  flex: 0 1 auto;
  padding-top: 0;
  width: auto;
}
.feature-line >>> .v-input__control {
  flex-grow: 0;
  width: auto;
}
.feature-expand {
  background: transparent;
  border: 0;
  cursor: pointer;
  height: 32px;
  margin-left: 2px;
  padding: 0;
  width: 32px;
}
.feature-detail {
  padding: 0 0 12px 46px;
  max-width: 720px;
}
</style>
